import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Button } from '@/components/ui/button'
import ServiceHeroVectorLines from '@/components/sections/ServiceHeroVectorLines'
import { ArrowRight, ChevronDown } from 'lucide-react'

interface ServiceHeroProps {
  serviceName: string
  titleLines: [string, string]
  description: string
  image?: string
}

export default function ServiceHero({
  serviceName,
  titleLines,
  description,
  image,
}: ServiceHeroProps) {
  const sectionRef = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  })
  const imageY = useTransform(scrollYProgress, [0, 1], ['-50%', '-25%'])
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '8%'])

  return (
    <section ref={sectionRef} className="relative min-h-[60vh] md:min-h-[65vh] w-full overflow-hidden bg-secondary flex flex-col justify-center">
      <motion.div
        aria-hidden
        style={{ y: bgY }}
        className="absolute inset-0 pointer-events-none select-none"
      >
        <div className="absolute inset-0 bg-primary/40" />
        <ServiceHeroVectorLines />
        <div
          className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-secondary/95"
          aria-hidden
        />
      </motion.div>
      {image && (
        <motion.img
          src={image}
          alt=""
          aria-hidden
          style={{ y: imageY }}
          className="absolute right-0 top-1/2 h-[120%] w-auto opacity-10 xl:opacity-20 pointer-events-none select-none object-contain"
        />
      )}
      <div className="container relative z-10 flex flex-col justify-center items-start py-36 md:py-48">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: 0 }}
          className="max-w-4xl"
        >
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-white/60 mb-6 uppercase tracking-wider">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/#services" className="hover:text-white transition-colors">Services</Link>
            <span>/</span>
            <span className="text-white">{serviceName}</span>
          </nav>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold font-heading text-white tracking-tighter leading-[0.9] mb-6 uppercase">
            {titleLines[0]}
            <br />
            <span className="text-accent">{titleLines[1]}</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-2xl mb-10 font-light leading-relaxed">
            {description}
          </p>

          <div className="flex flex-col sm:flex-row items-start gap-4 max-[400px]:gap-2.5">
            <Link to="/new-project">
              <Button size="lg" className="px-8 py-6 text-lg max-[400px]:h-auto max-[400px]:px-5 max-[400px]:py-3.5 max-[400px]:text-sm bg-accent text-white hover:bg-accent/90 focus:bg-accent/90 transition-all duration-300 border-0 rounded-full font-bold uppercase tracking-wider shadow-lg hover:shadow-xl hover:scale-105 focus:scale-105 max-[400px]:tracking-wide">
                Start a project
                <ArrowRight className="ml-2 h-5 w-5 max-[400px]:ml-1.5 max-[400px]:h-4 max-[400px]:w-4" />
              </Button>
            </Link>
            <a href="#pricing">
              <Button size="lg" variant="outline" className="px-8 py-6 text-lg max-[400px]:h-auto max-[400px]:px-5 max-[400px]:py-3.5 max-[400px]:text-sm max-[400px]:tracking-wide bg-transparent border-white/40 text-white hover:bg-white/10 hover:border-white/60 focus:bg-white/10 focus:border-white/60 rounded-full uppercase tracking-wider backdrop-blur-sm transition-all duration-300">
                View packages
              </Button>
            </a>
          </div>
        </motion.div>
      </div>

      <a href="#services" className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-10 cursor-pointer group">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="flex flex-col items-center gap-3"
        >
          <span className="text-xs text-white/60 group-hover:text-white group-focus:text-white uppercase tracking-[0.2em] font-bold transition-colors">Scroll</span>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <ChevronDown className="h-6 w-6 text-white/60" />
          </motion.div>
        </motion.div>
      </a>
    </section>
  )
}
